/**
 * Salaries — Liste dense employés (parité BE-TAYE SalariesList.jsx).
 * Framer Motion entrance + Export CSV + colonne contrat actif.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Search, Plus, Users, Download, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AIContractImportWizard from "@/components/hr/AIContractImportWizard";

const STATUS_BADGE = {
  active:     { cls: "bg-emerald-100 text-emerald-700 border-emerald-200", label: "Actif" },
  on_leave:   { cls: "bg-amber-100 text-amber-700 border-amber-200", label: "En congé" },
  terminated: { cls: "bg-slate-200 text-slate-700 border-slate-300", label: "Sorti" },
};

const CONTRACT_LABEL = {
  cdi: "CDI", cdd: "CDD", alternance: "Alternance", stage: "Stage", interim: "Intérim",
};

function NewEmployeeDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "",
    job_title: "", department: "", hire_date: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name) { toast.error("Prénom et nom requis"); return; }
    setSaving(true);
    try {
      await api.post("/hr/employees", form);
      toast.success("Salarié créé");
      onSaved?.(); onOpenChange(false);
      setForm({ first_name: "", last_name: "", email: "", job_title: "", department: "", hire_date: "" });
    } catch (e2) { toast.error(apiError(e2)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-employee-dialog">
        <DialogHeader><DialogTitle>Nouveau salarié</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prénom *</Label><Input value={form.first_name}
              onChange={(e) => setForm(p => ({ ...p, first_name: e.target.value }))} data-testid="emp-first-name" /></div>
            <div><Label>Nom *</Label><Input value={form.last_name}
              onChange={(e) => setForm(p => ({ ...p, last_name: e.target.value }))} data-testid="emp-last-name" /></div>
          </div>
          <div><Label>Email pro</Label><Input type="email" value={form.email}
            onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Poste</Label><Input value={form.job_title}
              onChange={(e) => setForm(p => ({ ...p, job_title: e.target.value }))} /></div>
            <div><Label>Service</Label><Input value={form.department}
              onChange={(e) => setForm(p => ({ ...p, department: e.target.value }))} /></div>
          </div>
          <div><Label>Date d'embauche</Label>
            <DatePickerFR value={form.hire_date} onChange={(v) => setForm(p => ({ ...p, hire_date: v }))} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-employee-btn">
              {saving ? "..." : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const exportCSV = (rows) => {
  const header = ["Prénom", "Nom", "Email", "Poste", "Service", "Type contrat", "Salaire brut annuel", "Embauche", "Statut"];
  const csv = [header.join(";")].concat(rows.map(e => [
    e.first_name || "", e.last_name || "", e.email || "", e.job_title || "", e.department || "",
    CONTRACT_LABEL[e.current_contract?.contract_type] || "",
    e.current_contract?.gross_annual_salary || "",
    e.hire_date || "",
    STATUS_BADGE[e.status]?.label || e.status || "",
  ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(";"))).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `salaries_${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export default function Salaries() {
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const canWrite = hasPermission("hr.write") || hasPermission("admin");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiWizardOpen, setAiWizardOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.search = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/hr/employees", { params });
      setRows(r.data.employees || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  return (
    <div className="space-y-6" data-testid="salaries-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Salariés
          </h1>
          <p className="text-sm text-slate-500">{rows.length} salarié{rows.length > 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => exportCSV(rows)} disabled={rows.length === 0}
            data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export CSV
          </Button>
          {canWrite && (
            <>
              <Button onClick={() => setAiWizardOpen(true)}
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                data-testid="ai-import-contract-btn">
                <Sparkles className="w-4 h-4 mr-1.5" /> Importer depuis contrat
              </Button>
              <Button onClick={() => setDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-employee-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Nouveau salarié
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Rechercher nom, email, poste..." className="pl-10" data-testid="salaries-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="salaries-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="active">Actifs</SelectItem>
                <SelectItem value="on_leave">En congé</SelectItem>
                <SelectItem value="terminated">Sortis</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="h-10 bg-slate-100 animate-pulse rounded" />)}</div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center" data-testid="salaries-empty">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">Aucun salarié. Cliquez sur "Nouveau salarié" pour commencer.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs uppercase text-slate-500">Nom</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Poste</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Contrat</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Email</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Embauche</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((e, i) => (
                  <motion.tr
                    key={e.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(i, 10) * 0.02, duration: 0.2 }}
                    data-testid={`employee-row-${i}`}
                    className="hover:bg-slate-50 cursor-pointer border-b"
                    onClick={() => navigate(`/app/hr/employees/${e.id}`)}
                  >
                    <TableCell>
                      <div className="font-medium text-slate-900">{e.first_name} {e.last_name}</div>
                      {e.department && <div className="text-xs text-slate-500">{e.department}</div>}
                    </TableCell>
                    <TableCell><span className="text-sm text-slate-700">{e.job_title || "—"}</span></TableCell>
                    <TableCell>
                      {e.current_contract ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {CONTRACT_LABEL[e.current_contract.contract_type] || e.current_contract.contract_type}
                        </Badge>
                      ) : <span className="text-xs text-slate-400">—</span>}
                    </TableCell>
                    <TableCell><span className="text-sm text-slate-500 font-mono">{e.email || "—"}</span></TableCell>
                    <TableCell><span className="text-sm">{e.hire_date ? new Date(e.hire_date).toLocaleDateString("fr-FR") : "—"}</span></TableCell>
                    <TableCell>
                      {(() => {
                        const cfg = STATUS_BADGE[e.status] || STATUS_BADGE.active;
                        return <Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge>;
                      })()}
                    </TableCell>
                  </motion.tr>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <NewEmployeeDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />

      <AIContractImportWizard
        open={aiWizardOpen}
        onOpenChange={setAiWizardOpen}
        mode="create"
        onDone={(res) => {
          load();
          if (res?.employee_id) {
            setTimeout(() => navigate(`/app/hr/employees/${res.employee_id}`), 500);
          }
        }}
      />
    </div>
  );
}
